import React, { useState } from 'react';
import './GoldCalculatorAdvanced.css';

// Interfaces
interface Scenario {
  id: string;
  name: string;
  description: string;
  goldRules: {
    startingGold: number;
    goldPerLevel: number;
    averageCSPerMinute: number;
    estimatedKills: number;
  };
}

interface LevelData {
  level: number;
  xpRequired: number;
  cumulativeXP: number;
}

interface WaveFarmingData {
  waveNumber: number;
  spawnTime: string;
  arrivalTime: string;
  clearTime: string;
  waveType: 'normal' | 'cannon';
  
  // XP Data
  waveXP: number;
  cumulativeXP: number;
  levelAtStart: number;
  levelAfterClear: number;
  levelUpThisWave: boolean;
  
  // Gold Data
  waveGold: number;
  passiveGold: number;
  cumulativeWaveGold: number;
  cumulativePassiveGold: number;
  totalEarnedGold: number;
  
  // Cannon upgrade info
  cannonUpgradeLevel?: number;
  cannonGoldValue?: number;
}

interface Props {
  scenario: Scenario;
}

type LaneType = 'solo' | 'duo';
type KillOrder = 'melee-cannon-caster' | 'caster-cannon-melee' | 'melee-caster-cannon';

const GoldCalculatorAdvanced: React.FC<Props> = ({ scenario }) => {
  const [laneType, setLaneType] = useState<LaneType>('solo');
  const [killOrder, setKillOrder] = useState<KillOrder>('melee-cannon-caster');
  const [maxWaves, setMaxWaves] = useState<number>(10);
  const [csPerMinute, setCSPerMinute] = useState<number | 'perfect' | 'custom'>('perfect');
  const [customCSPM, setCustomCSPM] = useState<string>('10');
  const [selectedWave, setSelectedWave] = useState<number>(1);

  // XP table data
  const levelData: LevelData[] = [
    { level: 1, xpRequired: 0, cumulativeXP: 0 },
    { level: 2, xpRequired: 280, cumulativeXP: 280 },
    { level: 3, xpRequired: 380, cumulativeXP: 660 },
    { level: 4, xpRequired: 480, cumulativeXP: 1140 },
    { level: 5, xpRequired: 580, cumulativeXP: 1720 },
    { level: 6, xpRequired: 680, cumulativeXP: 2400 },
    { level: 7, xpRequired: 780, cumulativeXP: 3180 },
    { level: 8, xpRequired: 880, cumulativeXP: 4060 },
    { level: 9, xpRequired: 980, cumulativeXP: 5040 },
    { level: 10, xpRequired: 1080, cumulativeXP: 6120 },
    { level: 11, xpRequired: 1180, cumulativeXP: 7300 },
    { level: 12, xpRequired: 1280, cumulativeXP: 8580 },
    { level: 13, xpRequired: 1380, cumulativeXP: 9960 },
    { level: 14, xpRequired: 1480, cumulativeXP: 11440 },
    { level: 15, xpRequired: 1580, cumulativeXP: 13020 },
    { level: 16, xpRequired: 1680, cumulativeXP: 14700 },
    { level: 17, xpRequired: 1780, cumulativeXP: 16480 },
    { level: 18, xpRequired: 1880, cumulativeXP: 18360 }
  ];

  // Base values
  const baseXPValues = { melee: 65, caster: 32, cannon: 100 };
  const baseGoldValues = { melee: 21, caster: 14 }; // Cannon gold is dynamic

  // Get XP sharing percentage
  const getXPSharingPercentage = (laneType: LaneType): number => {
    return laneType === 'solo' ? 0.95 : 0.62;
  };

  // Get actual XP values based on lane type
  const getActualXPValues = (laneType: LaneType) => {
    const sharingPercentage = getXPSharingPercentage(laneType);
    return {
      melee: Math.round(baseXPValues.melee * sharingPercentage),
      caster: Math.round(baseXPValues.caster * sharingPercentage),
      cannon: Math.round(baseXPValues.cannon * sharingPercentage)
    };
  };

  // Calculate cannon upgrade level based on game time
  const getCannonUpgradeLevel = (gameTimeSeconds: number): number => {
    const firstCannonSpawnTime = 155; // 2:35 in seconds
    
    if (gameTimeSeconds < firstCannonSpawnTime) {
      return 1;
    }
    
    const timeFromFirstCannon = gameTimeSeconds - firstCannonSpawnTime;
    const upgradeLevel = Math.floor(timeFromFirstCannon / 90) + 1;
    
    return Math.min(11, upgradeLevel);
  };

  // Calculate cannon gold value
  const getCannonGold = (upgradeLevel: number): number => {
    const baseGold = 57;
    const upgradeBonus = 3 * upgradeLevel;
    return Math.min(90, baseGold + upgradeBonus);
  };

  // Convert seconds to MM:SS format
  const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  // Determine wave type
  const getWaveType = (waveNumber: number): 'normal' | 'cannon' => {
    if (waveNumber <= 3) return 'normal';
    
    const wave28SpawnTime = 28;
    if (waveNumber <= 28) {
      const adjustedWave = waveNumber - 3;
      return (adjustedWave % 3 === 1) ? 'cannon' : 'normal';
    } else {
      return (waveNumber % 2 === 1) ? 'normal' : 'cannon';
    }
  };

  // Get minion kill order
  const getMinionKillOrder = (waveType: 'normal' | 'cannon'): ('melee' | 'caster' | 'cannon')[] => {
    const baseOrder = {
      'melee-cannon-caster': waveType === 'cannon' 
        ? ['melee', 'melee', 'melee', 'cannon', 'caster', 'caster', 'caster'] as const
        : ['melee', 'melee', 'melee', 'caster', 'caster', 'caster'] as const,
      'caster-cannon-melee': waveType === 'cannon'
        ? ['caster', 'caster', 'caster', 'cannon', 'melee', 'melee', 'melee'] as const
        : ['caster', 'caster', 'caster', 'melee', 'melee', 'melee'] as const,
      'melee-caster-cannon': waveType === 'cannon'
        ? ['melee', 'melee', 'melee', 'caster', 'caster', 'caster', 'cannon'] as const
        : ['melee', 'melee', 'melee', 'caster', 'caster', 'caster'] as const,
    };
    
    return [...baseOrder[killOrder]];
  };

  // Get current level based on XP
  const getLevelFromXP = (xp: number): number => {
    for (let i = levelData.length - 1; i >= 0; i--) {
      if (xp >= levelData[i].cumulativeXP) {
        return levelData[i].level;
      }
    }
    return 1;
  };

  // Get average gold per CS based on game time
  const getAverageGoldPerCS = (gameTimeMinutes: number): number => {
    if (gameTimeMinutes < 15) {
      return 19.8; // 0:00-15:00
    } else if (gameTimeMinutes < 17.75) { // 17:45
      return 22.6; // 15:00-17:45
    } else if (gameTimeMinutes < 25) {
      return 23.0; // 17:45-25:00
    } else {
      return 27.6; // 25:00+
    }
  };

  // Calculate expected gold based on CS per minute with proper time-based rates
  const calculateGoldFromCSPM = (cspm: number, gameTimeMinutes: number): number => {
    const farmingTimeMinutes = Math.max(0, gameTimeMinutes - 1.5); // Subtract 1:30 before first wave
    if (farmingTimeMinutes <= 0) return 0;
    
    let totalGold = 0;
    let remainingTime = farmingTimeMinutes;
    let currentTime = 1.5; // Start at 1:30 (first wave arrival)
    
    // Apply different rates for different time periods
    if (remainingTime > 0 && currentTime < 15) {
      const timeInPeriod = Math.min(remainingTime, 15 - currentTime);
      const csInPeriod = cspm * timeInPeriod;
      totalGold += csInPeriod * 19.8; // 0:00-15:00 rate
      remainingTime -= timeInPeriod;
      currentTime += timeInPeriod;
    }
    
    if (remainingTime > 0 && currentTime < 17.75) {
      const timeInPeriod = Math.min(remainingTime, 17.75 - currentTime);
      const csInPeriod = cspm * timeInPeriod;
      totalGold += csInPeriod * 22.6; // 15:00-17:45 rate
      remainingTime -= timeInPeriod;
      currentTime += timeInPeriod;
    }
    
    if (remainingTime > 0 && currentTime < 25) {
      const timeInPeriod = Math.min(remainingTime, 25 - currentTime);
      const csInPeriod = cspm * timeInPeriod;
      totalGold += csInPeriod * 23.0; // 17:45-25:00 rate
      remainingTime -= timeInPeriod;
      currentTime += timeInPeriod;
    }
    
    if (remainingTime > 0) {
      const csInPeriod = cspm * remainingTime;
      totalGold += csInPeriod * 27.6; // 25:00+ rate
    }
    
    return totalGold;
  };

  // Gold per CS reference data
  const goldPerCSData = [
    { timeRange: "0:00 - 15:00", goldPerCS: 19.8 },
    { timeRange: "15:00 - 17:45", goldPerCS: 22.6 },
    { timeRange: "17:45 - 25:00", goldPerCS: 23.0 },
    { timeRange: "25:00+", goldPerCS: 27.6 }
  ];

  // Generate comprehensive wave farming data
  const generateWaveFarmingData = (): WaveFarmingData[] => {
    const waves: WaveFarmingData[] = [];
    const actualXPValues = getActualXPValues(laneType);
    
    let cumulativeXP = 0;
    let cumulativeWaveGold = 0;
    let cumulativePassiveGold = 0;
    
    for (let waveNum = 1; waveNum <= maxWaves; waveNum++) {
      // Calculate timings
      const spawnTimeSeconds = 65 + (waveNum - 1) * 30;
      const arrivalTimeSeconds = spawnTimeSeconds + 25;
      const clearTimeSeconds = arrivalTimeSeconds + 30;
      
      const spawnTime = formatTime(spawnTimeSeconds);
      const arrivalTime = formatTime(arrivalTimeSeconds);
      const clearTime = formatTime(clearTimeSeconds);
      
      const waveType = getWaveType(waveNum);
      const levelAtStart = getLevelFromXP(cumulativeXP);
      
      // Calculate XP for this wave (always gained regardless of CS)
      const killOrderForWave = getMinionKillOrder(waveType);
      let waveXP = 0;
      
      killOrderForWave.forEach(minionType => {
        if (minionType === 'melee') {
          waveXP += actualXPValues.melee;
        } else if (minionType === 'caster') {
          waveXP += actualXPValues.caster;
        } else {
          waveXP += actualXPValues.cannon;
        }
      });
      
      cumulativeXP += waveXP;
      const levelAfterClear = getLevelFromXP(cumulativeXP);
      const levelUpThisWave = levelAfterClear > levelAtStart;
      
      // Calculate Gold for this wave based on CSPM
      let waveGold = 0;
      
      if (csPerMinute === 'perfect') {
        // Perfect CS - get all minions
        const cannonUpgradeLevel = getCannonUpgradeLevel(spawnTimeSeconds);
        const cannonGoldValue = getCannonGold(cannonUpgradeLevel);
        
        killOrderForWave.forEach(minionType => {
          if (minionType === 'melee') {
            waveGold += baseGoldValues.melee;
          } else if (minionType === 'caster') {
            waveGold += baseGoldValues.caster;
          } else {
            waveGold += cannonGoldValue;
          }
        });
      } else {
        // CSPM-based calculation (accounting for 1:30 before first wave)
        const currentCSPM = csPerMinute === 'custom' ? parseFloat(customCSPM) : (typeof csPerMinute === 'number' ? csPerMinute : 10);
        const gameTimeMinutes = clearTimeSeconds / 60;
        const expectedGoldFromCSPM = calculateGoldFromCSPM(currentCSPM, gameTimeMinutes);
        const previousGameTimeMinutes = Math.max(0, (clearTimeSeconds - 30) / 60);
        const previousGoldFromCSPM = calculateGoldFromCSPM(currentCSPM, previousGameTimeMinutes);
        
        waveGold = expectedGoldFromCSPM - previousGoldFromCSPM;
      }
      
      cumulativeWaveGold += waveGold;
      
      // Calculate passive gold
      const passiveStartTime = 100;
      let passiveGoldThisWave = 0;
      
      if (clearTimeSeconds > passiveStartTime) {
        const passiveTimeElapsed = clearTimeSeconds - passiveStartTime;
        const totalPassiveGoldSoFar = (passiveTimeElapsed / 10) * 20.4;
        passiveGoldThisWave = totalPassiveGoldSoFar - cumulativePassiveGold;
        cumulativePassiveGold = totalPassiveGoldSoFar;
      }
      
      const totalEarnedGold = cumulativeWaveGold + cumulativePassiveGold;
      
      const waveData: WaveFarmingData = {
        waveNumber: waveNum,
        spawnTime,
        arrivalTime,
        clearTime,
        waveType,
        
        // XP Data
        waveXP: Math.round(waveXP),
        cumulativeXP: Math.round(cumulativeXP),
        levelAtStart,
        levelAfterClear,
        levelUpThisWave,
        
        // Gold Data
        waveGold: Math.round(waveGold),
        passiveGold: Math.round(passiveGoldThisWave),
        cumulativeWaveGold: Math.round(cumulativeWaveGold),
        cumulativePassiveGold: Math.round(cumulativePassiveGold),
        totalEarnedGold: Math.round(totalEarnedGold)
      };
      
      // Add cannon upgrade info for perfect CS
      if (waveType === 'cannon' && csPerMinute === 'perfect') {
        const cannonUpgradeLevel = getCannonUpgradeLevel(spawnTimeSeconds);
        const cannonGoldValue = getCannonGold(cannonUpgradeLevel);
        waveData.cannonUpgradeLevel = cannonUpgradeLevel;
        waveData.cannonGoldValue = cannonGoldValue;
      }
      
      waves.push(waveData);
    }
    
    return waves;
  };

  const waveFarmingData = generateWaveFarmingData();

  // Get selected wave data for details view
  const getSelectedWaveData = () => {
    const waveData = waveFarmingData.find(wave => wave.waveNumber === selectedWave);
    if (!waveData) return null;

    // Convert to format expected by wave details
    return {
      waveNumber: waveData.waveNumber,
      spawnTime: waveData.spawnTime,
      arrivalTime: waveData.arrivalTime,
      clearTime: waveData.clearTime,
      waveType: waveData.waveType,
      goldValue: waveData.waveGold,
      passiveGoldEarned: waveData.passiveGold,
      totalGoldAtClear: waveData.totalEarnedGold,
      minionCount: {
        melee: waveData.waveType === 'cannon' ? 3 : 3,
        caster: waveData.waveType === 'cannon' ? 3 : 3,
        cannon: waveData.waveType === 'cannon' ? 1 : 0
      },
      cannonUpgradeLevel: waveData.cannonUpgradeLevel,
      cannonGoldValue: waveData.cannonGoldValue
    };
  };

  return (
    <div className="gold-calculator-advanced">
      <h5>Gold Calculator</h5>
      <p className="gold-description">
        Comprehensive gold and XP analysis showing your champion's progression after each wave clear.
      </p>
      
  {/* Interactive Gold Calculator */}
  <div className="interactive-gold-calculator">
        <h6>Gold Calculator</h6>
        <GoldCalculatorWidget />
      </div>

      {/* Settings */}
      <div className="farming-settings">
        <div className="setting-group">
          <label>Lane Type:</label>
          <select 
            value={laneType} 
            onChange={(e) => setLaneType(e.target.value as LaneType)}
            className="setting-dropdown"
          >
            <option value="solo">Solo Lane (95% XP)</option>
            <option value="duo">Duo Lane (62% XP)</option>
          </select>
        </div>
        
        <div className="setting-group">
          <label>Kill Order:</label>
          <select 
            value={killOrder} 
            onChange={(e) => setKillOrder(e.target.value as KillOrder)}
            className="setting-dropdown"
          >
            <option value="melee-cannon-caster">Melee → Cannon → Caster</option>
            <option value="caster-cannon-melee">Caster → Cannon → Melee</option>
            <option value="melee-caster-cannon">Melee → Caster → Cannon</option>
          </select>
        </div>
        
        <div className="setting-group">
          <label>CS per Minute:</label>
          <select 
            value={csPerMinute} 
            onChange={(e) => {
              const value = e.target.value;
              if (value === 'perfect') {
                setCSPerMinute('perfect');
              } else if (value === 'custom') {
                setCSPerMinute('custom');
              } else {
                setCSPerMinute(parseInt(value));
              }
            }}
            className="setting-dropdown"
          >
            <option value="perfect">Perfect CS</option>
            <option value={12}>12 CSPM</option>
            <option value={11}>11 CSPM</option>
            <option value={10}>10 CSPM</option>
            <option value={9}>9 CSPM</option>
            <option value={8}>8 CSPM</option>
            <option value={7}>7 CSPM</option>
            <option value={6}>6 CSPM</option>
            <option value="custom">Custom</option>
          </select>
          {csPerMinute === 'custom' && (
            <input
              type="number"
              value={customCSPM}
              onChange={(e) => setCustomCSPM(e.target.value)}
              className="custom-cspm-input"
              placeholder="Enter CSPM"
              min="0"
              max="20"
              step="0.1"
            />
          )}
        </div>
        
        <div className="setting-group">
          <label>Number of Waves:</label>
          <select 
            value={maxWaves} 
            onChange={(e) => setMaxWaves(parseInt(e.target.value))}
            className="setting-dropdown"
          >
            <option value={10}>10 Waves</option>
            <option value={20}>20 Waves</option>
            <option value={30}>30 Waves</option>
            <option value={50}>50 Waves</option>
            <option value={100}>100 Waves</option>
          </select>
        </div>
      </div>
      
      {/* Wave Farming Overview */}
      <div className="wave-farming-overview">
        <h6>Wave-by-Wave Progression</h6>
        <div className="farming-table">
          <div className="farming-table-header">
            <span>Wave</span>
            <span>Clear Time</span>
            <span>Type</span>
            <span>Level Start</span>
            <span>Level End</span>
            <span>Wave Gold</span>
            <span>Passive Gold</span>
            <span>Total Gold</span>
          </div>
          {waveFarmingData.map(wave => (
            <div 
              key={wave.waveNumber} 
              className={`farming-table-row ${selectedWave === wave.waveNumber ? 'selected' : ''}`}
              onClick={() => setSelectedWave(wave.waveNumber)}
              style={{ cursor: 'pointer' }}
            >
              <span className="wave-num">{wave.waveNumber}</span>
              <span className="clear-time">{wave.clearTime}</span>
              <span className={`wave-type ${wave.waveType}`}>
                {wave.waveType === 'cannon' ? (
                  <span className="cannon-icon">
                    🔫
                    {wave.cannonUpgradeLevel && (
                      <span className="upgrade-badge">{wave.cannonUpgradeLevel}</span>
                    )}
                  </span>
                ) : '⚔️'}
              </span>
              <span className="level-start">{wave.levelAtStart}</span>
              <span className={`level-end ${wave.levelUpThisWave ? 'level-up-highlight' : ''}`}>
                {wave.levelAfterClear}
                {wave.levelUpThisWave && <span className="level-up-arrow">↗</span>}
              </span>
              <span className="wave-gold">{wave.waveGold}g</span>
              <span className="passive-gold">{wave.passiveGold}g</span>
              <span className="total-gold">{wave.totalEarnedGold}g</span>
            </div>
          ))}
        </div>
        
        <div className="farming-controls">
          <div className="wave-info">
            <span>Showing {maxWaves} waves (up to {formatTime(65 + (maxWaves - 1) * 30 + 25 + 30)})</span>
          </div>
        </div>
      </div>
      
      {/* Wave Details */}
      {(() => {
        const selectedWaveData = getSelectedWaveData();
        if (!selectedWaveData) return null;
        
        return (
          <div className="wave-details">
            <div className="wave-header">
              <h6>Wave {selectedWaveData.waveNumber} - {selectedWaveData.waveType.charAt(0).toUpperCase() + selectedWaveData.waveType.slice(1)} Wave</h6>
            </div>
            
            <div className="wave-timing">
              <div className="timing-item">
                <span className="timing-label">Spawn:</span>
                <span className="timing-value">{selectedWaveData.spawnTime}</span>
              </div>
              <div className="timing-item">
                <span className="timing-label">Arrival:</span>
                <span className="timing-value">{selectedWaveData.arrivalTime}</span>
              </div>
              <div className="timing-item">
                <span className="timing-label">Cleared:</span>
                <span className="timing-value">{selectedWaveData.clearTime}</span>
              </div>
            </div>
            
            <div className="minion-composition">
              <h6>Minion Composition:</h6>
              <div className="minion-grid">
                <div className="minion-type">
                  <span className="minion-label">Melee:</span>
                  <span className="minion-count">{selectedWaveData.minionCount.melee}</span>
                  <div className="minion-gold-info">
                    <span className="minion-gold-calc">{selectedWaveData.minionCount.melee} × 21g</span>
                    <span className="minion-gold-total">{selectedWaveData.minionCount.melee * 21}g</span>
                  </div>
                </div>
                <div className="minion-type">
                  <span className="minion-label">Caster:</span>
                  <span className="minion-count">{selectedWaveData.minionCount.caster}</span>
                  <div className="minion-gold-info">
                    <span className="minion-gold-calc">{selectedWaveData.minionCount.caster} × 14g</span>
                    <span className="minion-gold-total">{selectedWaveData.minionCount.caster * 14}g</span>
                  </div>
                </div>
                {selectedWaveData.minionCount.cannon > 0 && (
                  <div className="minion-type cannon">
                    <span className="minion-label">Cannon:</span>
                    <span className="minion-count">{selectedWaveData.minionCount.cannon}</span>
                    <div className="minion-gold-info">
                      <span className="minion-gold-calc">{selectedWaveData.minionCount.cannon} × {selectedWaveData.cannonGoldValue}g</span>
                      <span className="minion-gold-total">{selectedWaveData.cannonGoldValue}g</span>
                    </div>
                    {selectedWaveData.cannonUpgradeLevel && (
                      <div className="cannon-upgrade-info">
                        <span className="upgrade-level">Lv.{selectedWaveData.cannonUpgradeLevel}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            
            <div className="gold-breakdown">
              <div className="breakdown-grid">
                <div className="breakdown-item">
                  <span className="breakdown-label">Wave Gold:</span>
                  <span className="breakdown-value">{selectedWaveData.goldValue}g</span>
                </div>
                <div className="breakdown-item">
                  <span className="breakdown-label">Passive Gold:</span>
                  <span className="breakdown-value">{selectedWaveData.passiveGoldEarned}g</span>
                </div>
                <div className="breakdown-item total">
                  <span className="breakdown-label">Total Gold:</span>
                  <span className="breakdown-value">{selectedWaveData.totalGoldAtClear}g</span>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      
      {/* Gold per CS Reference Table */}
      <div className="gold-per-cs-reference">
        <h6>Average Gold per CS Over Time</h6>
        <div className="gold-per-cs-table">
          <div className="gold-per-cs-header">
            <span>Time Range</span>
            <span>Gold per CS</span>
          </div>
          {goldPerCSData.map((data, index) => (
            <div key={index} className="gold-per-cs-row">
              <span className="time-range">{data.timeRange}</span>
              <span className="gold-value">{data.goldPerCS}g</span>
            </div>
          ))}
        </div>
      </div>

      {/* Wave-by-Wave CSPM Comparison */}
      <div className="cs-comparison">
        <h6>CS per Minute Comparison (Cumulative Gold)</h6>
        <div className="cspm-comparison-table">
          <div className="cspm-comparison-header">
            <span>Wave</span>
            <span>Time</span>
            <span>Wave Value</span>
            <span>Passive</span>
            <span>Perfect CS Total</span>
            <span>12 CSPM Total</span>
            <span>%</span>
            <span>11 CSPM Total</span>
            <span>%</span>
            <span>10 CSPM Total</span>
            <span>%</span>
            <span>9 CSPM Total</span>
            <span>%</span>
            <span>8 CSPM Total</span>
            <span>%</span>
            <span>7 CSPM Total</span>
            <span>%</span>
            <span>6 CSPM Total</span>
            <span>%</span>
            <span>5 CSPM Total</span>
            <span>%</span>
            <span>4 CSPM Total</span>
            <span>%</span>
          </div>
          {waveFarmingData.slice(0, maxWaves).map(wave => {
            // Calculate cumulative gold for different CSPM values
            // Convert time string to seconds for calculation
            const [minutes, seconds] = wave.clearTime.split(':').map(Number);
            const clearTimeSeconds = minutes * 60 + seconds;
            const gameTimeMinutes = clearTimeSeconds / 60;
            
            const perfectTotal = wave.cumulativeWaveGold + wave.cumulativePassiveGold;
            
            const cspmTotalValues = {
              12: Math.round(calculateGoldFromCSPM(12, gameTimeMinutes) + wave.cumulativePassiveGold),
              11: Math.round(calculateGoldFromCSPM(11, gameTimeMinutes) + wave.cumulativePassiveGold),
              10: Math.round(calculateGoldFromCSPM(10, gameTimeMinutes) + wave.cumulativePassiveGold),
              9: Math.round(calculateGoldFromCSPM(9, gameTimeMinutes) + wave.cumulativePassiveGold),
              8: Math.round(calculateGoldFromCSPM(8, gameTimeMinutes) + wave.cumulativePassiveGold),
              7: Math.round(calculateGoldFromCSPM(7, gameTimeMinutes) + wave.cumulativePassiveGold),
              6: Math.round(calculateGoldFromCSPM(6, gameTimeMinutes) + wave.cumulativePassiveGold),
              5: Math.round(calculateGoldFromCSPM(5, gameTimeMinutes) + wave.cumulativePassiveGold),
              4: Math.round(calculateGoldFromCSPM(4, gameTimeMinutes) + wave.cumulativePassiveGold)
            };
            
            const cspmPercentages = {
              12: Math.round((cspmTotalValues[12] / perfectTotal) * 100),
              11: Math.round((cspmTotalValues[11] / perfectTotal) * 100),
              10: Math.round((cspmTotalValues[10] / perfectTotal) * 100),
              9: Math.round((cspmTotalValues[9] / perfectTotal) * 100),
              8: Math.round((cspmTotalValues[8] / perfectTotal) * 100),
              7: Math.round((cspmTotalValues[7] / perfectTotal) * 100),
              6: Math.round((cspmTotalValues[6] / perfectTotal) * 100),
              5: Math.round((cspmTotalValues[5] / perfectTotal) * 100),
              4: Math.round((cspmTotalValues[4] / perfectTotal) * 100)
            };
            
            return (
              <div key={wave.waveNumber} className="cspm-comparison-row">
                <span className="wave-num">{wave.waveNumber}</span>
                <span className="clear-time">{wave.clearTime}</span>
                <span className="wave-value">{wave.cumulativeWaveGold}g</span>
                <span className="passive-gold">{wave.cumulativePassiveGold}g</span>
                <span className="perfect-total">{perfectTotal}g</span>
                <span className="cspm-total">{cspmTotalValues[12]}g</span>
                <span className="percentage">{cspmPercentages[12]}%</span>
                <span className="cspm-total">{cspmTotalValues[11]}g</span>
                <span className="percentage">{cspmPercentages[11]}%</span>
                <span className="cspm-total">{cspmTotalValues[10]}g</span>
                <span className="percentage">{cspmPercentages[10]}%</span>
                <span className="cspm-total">{cspmTotalValues[9]}g</span>
                <span className="percentage">{cspmPercentages[9]}%</span>
                <span className="cspm-total">{cspmTotalValues[8]}g</span>
                <span className="percentage">{cspmPercentages[8]}%</span>
                <span className="cspm-total">{cspmTotalValues[7]}g</span>
                <span className="percentage">{cspmPercentages[7]}%</span>
                <span className="cspm-total">{cspmTotalValues[6]}g</span>
                <span className="percentage">{cspmPercentages[6]}%</span>
                <span className="cspm-total">{cspmTotalValues[5]}g</span>
                <span className="percentage">{cspmPercentages[5]}%</span>
                <span className="cspm-total">{cspmTotalValues[4]}g</span>
                <span className="percentage">{cspmPercentages[4]}%</span>
              </div>
            );
          })}
        </div>
        <div className="cspm-comparison-controls">
          <div className="wave-info">
            <span>Showing {maxWaves} waves in comparison table</span>
          </div>
        </div>
        <div className="cspm-description">
          <p><strong>How CSPM is calculated:</strong> CS per minute values show gold earned from minions only, excluding passive income. 
          Calculations start from 1:30 (first wave arrival) since no farming is possible before then. 
          Percentages compare CSPM gold to perfect CS wave value.</p>
        </div>
      </div>
  
      
    
    </div>
  );
};

// Interfaces for the enhanced gold calculator
interface Kill {
  id: string;
  value: number;
}

interface Turret {
  id: string;
  type: 'outer' | 'inner-mid' | 'inner-side' | 'inhibitor' | 'nexus';
  localGold: number;
  globalGold: number;
}

interface TurretPlate {
  count: number;
  goldPerPlate: number;
}

type CalculatorMode = 'accumulative' | 'target';

// Interactive Gold Calculator Widget Component
const GoldCalculatorWidget: React.FC = () => {
  // Mode selection
  const [mode, setMode] = useState<CalculatorMode>('accumulative');
  
  // Accumulative mode states
  const [csPerMinute, setCSPerMinute] = useState<number>(10);
  const [gameTimeMinutes, setGameTimeMinutes] = useState<number>(15);
  const [kills, setKills] = useState<Kill[]>([{ id: '1', value: 300 }]);
  const [turrets, setTurrets] = useState<Turret[]>([]);
  const [turretPlates, setTurretPlates] = useState<TurretPlate>({ count: 0, goldPerPlate: 125 });
  const [miscellaneousGold, setMiscellaneousGold] = useState<number>(0);
  const [baronBuff, setBaronBuff] = useState<boolean>(false);
  
  // Target mode states
  const [targetGold, setTargetGold] = useState<number>(1000);
  const [includeKills, setIncludeKills] = useState<boolean>(false);
  const [includeTurrets, setIncludeTurrets] = useState<boolean>(false);
  const [includeMiscellaneous, setIncludeMiscellaneous] = useState<boolean>(false);

  // Turret definitions
  const turretTypes = [
    { type: 'outer' as const, name: 'Outer (T1)', localGold: 250, globalGold: 50 },
    { type: 'inner-mid' as const, name: 'Inner Mid (T2)', localGold: 425, globalGold: 25 },
    { type: 'inner-side' as const, name: 'Inner Side (T2)', localGold: 675, globalGold: 25 },
    { type: 'inhibitor' as const, name: 'Inhibitor (T3)', localGold: 375, globalGold: 25 },
    { type: 'nexus' as const, name: 'Nexus (T4)', localGold: 0, globalGold: 50 }
  ];

  // Get average gold per CS based on game time
  const getAverageGoldPerCS = (gameTimeMinutes: number): number => {
    if (gameTimeMinutes < 15) {
      return 19.8;
    } else if (gameTimeMinutes < 17.75) {
      return 22.6;
    } else if (gameTimeMinutes < 25) {
      return 23.0;
    } else {
      return 27.6;
    }
  };

  // Calculate gold from CS
  const calculateGoldFromCS = (cspm: number, timeMinutes: number): number => {
    const farmingTime = Math.max(0, timeMinutes - 1.5);
    const totalCS = cspm * farmingTime;
    const averageGoldPerCS = getAverageGoldPerCS(timeMinutes);
    return totalCS * averageGoldPerCS;
  };

  // Calculate passive gold
  const calculatePassiveGold = (timeMinutes: number): number => {
    const passiveStartTime = 100; // 1:40 in seconds
    const timeSeconds = timeMinutes * 60;
    if (timeSeconds > passiveStartTime) {
      const passiveTime = timeSeconds - passiveStartTime;
      return (passiveTime / 10) * 20.4;
    }
    return 0;
  };

  // Kill management functions
  const addKill = () => {
    const newId = (kills.length + 1).toString();
    setKills([...kills, { id: newId, value: 300 }]);
  };

  const removeKill = (id: string) => {
    setKills(kills.filter(kill => kill.id !== id));
  };

  const updateKillValue = (id: string, value: number) => {
    setKills(kills.map(kill => kill.id === id ? { ...kill, value: Math.max(100, Math.min(1000, value)) } : kill));
  };

  // Turret management functions
  const addTurret = (type: Turret['type']) => {
    const turretType = turretTypes.find(t => t.type === type);
    if (!turretType) return;
    
    const newId = `${type}-${Date.now()}`;
    const newTurret: Turret = {
      id: newId,
      type,
      localGold: turretType.localGold,
      globalGold: turretType.globalGold
    };
    setTurrets([...turrets, newTurret]);
  };

  const removeTurret = (id: string) => {
    setTurrets(turrets.filter(turret => turret.id !== id));
  };

  // Calculate total gold for accumulative mode
  const calculateAccumulativeGold = () => {
    const csGold = calculateGoldFromCS(csPerMinute, gameTimeMinutes);
    const passiveGold = calculatePassiveGold(gameTimeMinutes);
    const killGold = kills.reduce((sum, kill) => sum + kill.value, 0);
    const turretGold = turrets.reduce((sum, turret) => sum + turret.localGold + turret.globalGold, 0);
    const plateGold = turretPlates.count * turretPlates.goldPerPlate;
    const baronGold = baronBuff && gameTimeMinutes >= 25 ? 300 : 0;
    
    return {
      csGold: Math.round(csGold),
      passiveGold: Math.round(passiveGold),
      killGold,
      turretGold,
      plateGold,
      miscellaneousGold,
      baronGold,
      total: Math.round(csGold + passiveGold + killGold + turretGold + plateGold + miscellaneousGold + baronGold)
    };
  };

  // Calculate time needed for target mode
  const calculateTimeForTarget = (cspm: number, targetGold: number): { timeNeeded: number, csGold: number, passiveGold: number, totalCS: number } => {
    // Subtract additional gold sources if included
    let adjustedTarget = targetGold;
    
    if (includeKills) {
      adjustedTarget -= kills.reduce((sum, kill) => sum + kill.value, 0);
    }
    if (includeTurrets) {
      adjustedTarget -= turrets.reduce((sum, turret) => sum + turret.localGold + turret.globalGold, 0);
      adjustedTarget -= turretPlates.count * turretPlates.goldPerPlate;
    }
    if (includeMiscellaneous) {
      adjustedTarget -= miscellaneousGold;
    }
    
    if (adjustedTarget <= 0) {
      return { timeNeeded: 0, csGold: 0, passiveGold: 0, totalCS: 0 };
    }

    // Binary search to find the time needed
    let low = 1.5; // Minimum time (first wave arrival)
    let high = 30; // Maximum time to search (30 minutes)
    let bestTime = high;
    
    for (let iterations = 0; iterations < 50; iterations++) {
      const mid = (low + high) / 2;
      
      const csGold = calculateGoldFromCS(cspm, mid);
      const passiveGold = calculatePassiveGold(mid);
      const totalGold = csGold + passiveGold;
      
      if (totalGold >= adjustedTarget) {
        bestTime = mid;
        high = mid;
      } else {
        low = mid;
      }
      
      if (high - low < 0.01) break;
    }
    
    // Calculate final values at bestTime
    const farmingTime = Math.max(0, bestTime - 1.5);
    const totalCS = cspm * farmingTime;
    const csGold = calculateGoldFromCS(cspm, bestTime);
    const passiveGold = calculatePassiveGold(bestTime);
    
    return {
      timeNeeded: Math.round(bestTime * 10) / 10,
      csGold: Math.round(csGold),
      passiveGold: Math.round(passiveGold),
      totalCS: Math.round(totalCS)
    };
  };

  const accumulativeResults = mode === 'accumulative' ? calculateAccumulativeGold() : null;
  const targetResults = mode === 'target' ? [12, 11, 10, 9, 8, 7, 6, 5, 4].map(cspm => ({
    cspm,
    ...calculateTimeForTarget(cspm, targetGold)
  })) : null;

  return (
    <div className="gold-calculator-widget">
      {/* Mode Selector */}
      <div className="calculator-mode-selector">
        <button 
          className={mode === 'accumulative' ? 'active' : ''}
          onClick={() => setMode('accumulative')}
        >
          Calculate Total Gold
        </button>
        <button 
          className={mode === 'target' ? 'active' : ''}
          onClick={() => setMode('target')}
        >
          Reach Target Gold
        </button>
      </div>

      {/* Explanation */}
      <div className="calculator-explanation">
        {mode === 'accumulative' ? (
          <div>
            <p>Set your farming parameters and additional gold sources to see your total gold income.</p>
            <p><strong>Tip:</strong> Adjust kills (100-1000g based on bounties), add turrets you've taken, count turret plates (max 15), and include miscellaneous gold from runes, abilities, etc.</p>
          </div>
        ) : (
          <div>
            <p>Enter a target gold amount and see how long it takes to reach with different CS rates.</p>
            <p><strong>Tip:</strong> Check the boxes below to subtract additional gold sources from your target, showing only the farming time needed.</p>
          </div>
        )}
      </div>

      {mode === 'accumulative' ? (
        /* Accumulative Mode */
        <div className="accumulative-mode">
          {/* Base Farming Settings */}
          <div className="farming-inputs">
            <div className="input-row">
              <div className="input-group">
                <label>CS per Minute:</label>
                <input
                  type="number"
                  value={csPerMinute}
                  onChange={(e) => setCSPerMinute(Math.max(0, parseFloat(e.target.value) || 0))}
                  min="0"
                  max="15"
                  step="0.5"
                  className="cspm-input"
                />
              </div>
              <div className="input-group">
                <label>Game Time (minutes):</label>
                <input
                  type="number"
                  value={gameTimeMinutes}
                  onChange={(e) => setGameTimeMinutes(Math.max(1.5, parseFloat(e.target.value) || 1.5))}
                  min="1.5"
                  max="60"
                  step="0.5"
                  className="time-input"
                />
              </div>
            </div>
            
            {/* Farming Statistics */}
            <div className="farming-stats">
              <div className="stat-item">
                <span className="stat-label">Waves:</span>
                <span className="stat-value">{Math.floor((gameTimeMinutes - 1.5) * 2)}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">CS Count:</span>
                <span className="stat-value">{Math.round(csPerMinute * Math.max(0, gameTimeMinutes - 1.5))}</span>
              </div>
              <div className="stat-item">
                <span className="stat-label">Farming Gold:</span>
                <span className="stat-value">{Math.round(calculateGoldFromCS(csPerMinute, gameTimeMinutes))}g</span>
              </div>
            </div>
          </div>

          {/* Kills Section */}
          <div className="gold-source-section">
            <h6>Kills ({kills.length})</h6>
            <div className="kills-container">
              {kills.map(kill => (
                <div key={kill.id} className="kill-item">
                  <input
                    type="range"
                    min="100"
                    max="1000"
                    step="25"
                    value={kill.value}
                    onChange={(e) => updateKillValue(kill.id, parseInt(e.target.value))}
                    className="kill-slider"
                  />
                  <input
                    type="number"
                    value={kill.value}
                    onChange={(e) => updateKillValue(kill.id, parseInt(e.target.value) || 300)}
                    min="100"
                    max="1000"
                    className="kill-value-input"
                  />
                  <span className="kill-gold">{kill.value}g</span>
                  <button onClick={() => removeKill(kill.id)} className="remove-kill-btn">×</button>
                </div>
              ))}
              <button onClick={addKill} className="add-kill-btn">+ Add Kill</button>
            </div>
            {kills.length > 0 && (
              <div className="gold-summary">
                <span className="summary-label">Total Kill Gold:</span>
                <span className="summary-value">{kills.reduce((sum, kill) => sum + kill.value, 0)}g</span>
              </div>
            )}
          </div>

          {/* Turrets Section */}
          <div className="gold-source-section">
            <h6>Turrets & Plates</h6>
            <div className="turret-info">
              <p><strong>Note:</strong> You get Local + Global gold when you participate in taking a turret. Values below show your personal gold gain.</p>
            </div>

            {/* Turret Plates Section */}
            <div className="plates-subsection">
              <h6>Turret Plates</h6>
              <div className="plate-controls">
                <div className="input-group">
                  <label>Plates Destroyed (max 15):</label>
                  <input
                    type="number"
                    value={turretPlates.count}
                    onChange={(e) => setTurretPlates({...turretPlates, count: Math.max(0, Math.min(15, parseInt(e.target.value) || 0))})}
                    min="0"
                    max="15"
                    className="plate-count-input"
                  />
                </div>
                <span className="plate-total">{turretPlates.count * turretPlates.goldPerPlate}g</span>
              </div>
              <div className="plate-info">
                <p>Each plate: 125g • Only on Outer (T1) turrets • 5 plates per turret</p>
              </div>
            </div>

            {/* Turret Types with Gold Values */}
            <div className="turrets-subsection">
              <h6>Full Turrets</h6>
              <div className="turret-values-display">
                {turretTypes.map(turretType => (
                  <div key={turretType.type} className="turret-value-row">
                    <span className="turret-type-name">{turretType.name}:</span>
                    <span className="turret-gold-breakdown">
                      {turretType.localGold}g (local) + {turretType.globalGold}g (global) = {turretType.localGold + turretType.globalGold}g
                    </span>
                    <button
                      onClick={() => addTurret(turretType.type)}
                      className="add-turret-btn-small"
                    >
                      +
                    </button>
                  </div>
                ))}
              </div>
            </div>

            {turrets.length > 0 && (
              <div className="turret-list">
                <h6>Selected Turrets:</h6>
                {turrets.map(turret => {
                  const turretType = turretTypes.find(t => t.type === turret.type);
                  const totalGold = turret.localGold + turret.globalGold;
                  return (
                    <div key={turret.id} className="turret-item">
                      <span className="turret-name">{turretType?.name}</span>
                      <span className="turret-gold">{totalGold}g</span>
                      <button onClick={() => removeTurret(turret.id)} className="remove-turret-btn">×</button>
                    </div>
                  );
                })}
              </div>
            )}

            {(turrets.length > 0 || turretPlates.count > 0) && (
              <div className="gold-summary">
                <span className="summary-label">Total Turret Gold:</span>
                <span className="summary-value">
                  {turrets.reduce((sum, turret) => sum + turret.localGold + turret.globalGold, 0) + turretPlates.count * turretPlates.goldPerPlate}g
                </span>
              </div>
            )}
          </div>

          {/* Miscellaneous Section */}
          <div className="gold-source-section">
            <h6>Miscellaneous</h6>
            <div className="input-group">
              <label>Additional Gold (First Strike, etc.):</label>
              <input
                type="number"
                value={miscellaneousGold}
                onChange={(e) => setMiscellaneousGold(Math.max(0, parseInt(e.target.value) || 0))}
                min="0"
                className="misc-gold-input"
              />
            </div>
            
            {/* Baron Buff Toggle */}
            {gameTimeMinutes >= 25 && (
              <div className="baron-section">
                <label className="baron-toggle">
                  <input
                    type="checkbox"
                    checked={baronBuff}
                    onChange={(e) => setBaronBuff(e.target.checked)}
                  />
                  Baron Buff (+300g)
                </label>
                <div className="baron-info">
                  <p>Baron spawns at 25:00 and gives 300 gold when killed</p>
                </div>
              </div>
            )}
          </div>

          {/* Results */}
          {accumulativeResults && (
            <div className="accumulative-results">
              <h6>Gold Breakdown</h6>
              <div className="results-grid">
                <div className="result-item">
                  <span className="result-label">CS Gold:</span>
                  <span className="result-value cs">{accumulativeResults.csGold}g</span>
                </div>
                <div className="result-item">
                  <span className="result-label">Passive Gold:</span>
                  <span className="result-value passive">{accumulativeResults.passiveGold}g</span>
                </div>
                <div className="result-item">
                  <span className="result-label">Kill Gold:</span>
                  <span className="result-value kills">{accumulativeResults.killGold}g</span>
                </div>
                <div className="result-item">
                  <span className="result-label">Turret Gold:</span>
                  <span className="result-value turrets">{accumulativeResults.turretGold}g</span>
                </div>
                <div className="result-item">
                  <span className="result-label">Plate Gold:</span>
                  <span className="result-value plates">{accumulativeResults.plateGold}g</span>
                </div>
                <div className="result-item">
                  <span className="result-label">Miscellaneous:</span>
                  <span className="result-value misc">{accumulativeResults.miscellaneousGold}g</span>
                </div>
                {accumulativeResults.baronGold > 0 && (
                  <div className="result-item">
                    <span className="result-label">Baron Buff:</span>
                    <span className="result-value baron">{accumulativeResults.baronGold}g</span>
                  </div>
                )}
                <div className="result-item total">
                  <span className="result-label">Total Gold:</span>
                  <span className="result-value total">{accumulativeResults.total}g</span>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Target Mode */
        <div className="target-mode">
          <div className="target-inputs">
        <div className="input-group">
          <label>Target Gold:</label>
          <input
            type="number"
            value={targetGold}
            onChange={(e) => setTargetGold(Math.max(0, parseFloat(e.target.value) || 0))}
            min="0"
            step="50"
            className="target-gold-input"
          />
        </div>
      </div>
      
          {/* Include Additional Sources */}
          <div className="target-options">
            <h6>Include Additional Gold Sources:</h6>
            <div className="option-checkboxes">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={includeKills}
                  onChange={(e) => setIncludeKills(e.target.checked)}
                />
                Kills ({kills.reduce((sum, kill) => sum + kill.value, 0)}g)
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={includeTurrets}
                  onChange={(e) => setIncludeTurrets(e.target.checked)}
                />
                Turrets & Plates ({turrets.reduce((sum, turret) => sum + turret.localGold + turret.globalGold, 0) + turretPlates.count * turretPlates.goldPerPlate}g)
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={includeMiscellaneous}
                  onChange={(e) => setIncludeMiscellaneous(e.target.checked)}
                />
                Miscellaneous ({miscellaneousGold}g)
              </label>
            </div>
          </div>
          
          {targetResults && (
      <div className="cspm-time-table">
        <div className="cspm-time-header">
          <span>CSPM</span>
          <span>Time</span>
          <span>CS Gold</span>
          <span>Passive</span>
          <span>Total</span>
          <span>CS Count</span>
        </div>
              {targetResults.map(result => (
          <div key={result.cspm} className="cspm-time-row">
            <span className="cspm-value">{result.cspm}</span>
            <span className="time-value">{result.timeNeeded}m</span>
            <span className="cs-gold-value">{result.csGold}g</span>
            <span className="passive-value">{result.passiveGold}g</span>
            <span className="total-value">{result.csGold + result.passiveGold}g</span>
            <span className="cs-count">{result.totalCS} CS</span>
          </div>
        ))}
      </div>
          )}
        </div>
      )}
    </div>
  );
};

export default GoldCalculatorAdvanced;
